import React, { Fragment } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import Breadcrumb from '../common/breadcrumb';
import { Redirect } from 'react-router';
import { X } from 'react-feather';
import { getDocById } from './docsData';
import { prepareDocHtml } from './prepareDocHtml';
import { scrollPageToTop } from './scrollPageToTop';

import '../../assets/scss/app-styles/users.scss';
import './docs.scss';

class DocsDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            backToDocs: false,
            iframeNonce: 0,
        };
        this.topRef = React.createRef();
        this.iframeRef = React.createRef();
    }

    componentDidMount() {
        scrollPageToTop(this.topRef.current);
        window.addEventListener('message', this.onDocMessage);
        this.scrollIframeToHash();
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.match && prevProps.match.params && prevProps.match.params.docId;
        const nextId = this.props.match && this.props.match.params && this.props.match.params.docId;
        const prevHash = prevProps.location && prevProps.location.hash;
        const nextHash = this.props.location && this.props.location.hash;

        if (prevId !== nextId) {
            scrollPageToTop(this.topRef.current);
            this.setState((state) => ({ iframeNonce: state.iframeNonce + 1 }));
        }

        if (prevId !== nextId || prevHash !== nextHash) {
            this.scrollIframeToHash();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.onDocMessage);
    }

    onDocMessage = (event) => {
        const data = event.data;
        if (!data || data.source !== 'vtsl-docs' || data.type !== 'navigate' || !data.docId) {
            return;
        }

        if (!getDocById(data.docId)) {
            return;
        }

        const currentId =
            (this.props.match && this.props.match.params && this.props.match.params.docId) || '';
        const hash = data.hash ? `#${data.hash}` : '';
        const nextPath = `${process.env.PUBLIC_URL}/docs/${data.docId}${hash}`;
        const currentPath = `${process.env.PUBLIC_URL}/docs/${currentId}${
            (this.props.location && this.props.location.hash) || ''
        }`;

        if (nextPath !== currentPath) {
            this.props.history.push(nextPath);
        }
    };

    onIframeLoad = () => {
        const frame = this.iframeRef.current;
        if (!frame) {
            return;
        }

        try {
            const href = frame.contentWindow && frame.contentWindow.location.href;
            // If the iframe left srcdoc and loaded the app (or anything else), reset it.
            if (href && href.indexOf('about:srcdoc') === -1 && href !== 'about:blank') {
                this.setState((state) => ({ iframeNonce: state.iframeNonce + 1 }));
                return;
            }
        } catch (err) {
            // Cross-origin access means it navigated away from srcdoc — reset.
            this.setState((state) => ({ iframeNonce: state.iframeNonce + 1 }));
            return;
        }

        this.scrollIframeToHash();
    };

    scrollIframeToHash = () => {
        const hash =
            (this.props.location && this.props.location.hash && this.props.location.hash.slice(1)) ||
            '';
        if (!hash) {
            return;
        }

        const tryScroll = () => {
            const frame = this.iframeRef.current;
            if (!frame || !frame.contentDocument) {
                return;
            }
            const target = frame.contentDocument.getElementById(hash);
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView();
            }
        };

        setTimeout(tryScroll, 50);
        setTimeout(tryScroll, 200);
    };

    goBack = () => {
        scrollPageToTop();
        this.setState({ backToDocs: true });
    };

    openInNewTab = (docId) => {
        window.open(`${process.env.PUBLIC_URL}/docs/${docId}`, '_blank');
    };

    render() {
        if (this.state.backToDocs) {
            return <Redirect to={`${process.env.PUBLIC_URL}/docs`} />;
        }

        const docId =
            (this.props.match && this.props.match.params && this.props.match.params.docId) || null;

        if (!docId) {
            return <Redirect to={`${process.env.PUBLIC_URL}/docs`} />;
        }

        const doc = getDocById(docId);
        if (!doc) {
            return <Redirect to={`${process.env.PUBLIC_URL}/docs`} />;
        }

        const html = prepareDocHtml(doc.html);

        return (
            <Fragment>
                <div ref={this.topRef} />
                <Breadcrumb
                    title={doc.title}
                    parent="Docs"
                    parent_url="docs"
                />
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-sm-12">
                            <Card>
                                <CardHeader className="docs-detail-header">
                                    <div>
                                        <h5>{doc.title}</h5>
                                        <span>{doc.summary}</span>
                                    </div>
                                    <div className="docs-detail-actions">
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={() => this.openInNewTab(doc.id)}
                                        >
                                            Open in new tab
                                        </button>
                                        <button
                                            type="button"
                                            className="docs-close-btn"
                                            title="Back to Docs"
                                            onClick={this.goBack}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </CardHeader>
                                <CardBody className="docs-detail-body">
                                    <iframe
                                        key={`${doc.id}-${this.state.iframeNonce}`}
                                        ref={this.iframeRef}
                                        title={doc.title}
                                        srcDoc={html}
                                        className="docs-iframe"
                                        sandbox="allow-scripts allow-same-origin"
                                        onLoad={this.onIframeLoad}
                                    />
                                </CardBody>
                            </Card>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default DocsDetails;
